import type {
  AuthTokenResult,
  AuthUser,
  FeJwtResponse,
  ResolvedConfig,
  TokenResponse,
  UserInfoResponse,
} from './types.js';
import { AuthError } from './errors.js';
import { resolveCompanyIdFromClaims } from './session/jwt.js';

export type FetchFn = typeof fetch;

function basicAuthHeader(clientId: string, clientSecret: string): string {
  return `Basic ${Buffer.from(`${clientId}:${clientSecret}`, 'utf8').toString('base64')}`;
}

export function parseFeJwt(body: FeJwtResponse): string {
  const fejwt = body.fejwt ?? body.fewjwt ?? body.token ?? body.jwt ?? body.access_token;
  if (!fejwt || typeof fejwt !== 'string') {
    throw new AuthError('fejwt', 'FEJWT missing in response');
  }
  return fejwt;
}

function parseAuthTokenResponse(body: FeJwtResponse & TokenResponse): AuthTokenResult {
  const accessToken = parseFeJwt(body);
  const refreshToken =
    typeof body.refresh_token === 'string' ? body.refresh_token : undefined;
  return { accessToken, refreshToken };
}

async function postAuthEndpoint(
  fetchFn: FetchFn,
  url: string,
  bearerToken: string,
): Promise<AuthTokenResult> {
  const res = await fetchFn(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({}),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new AuthError('fejwt', 'Auth token request failed', {
      details: { status: res.status, body },
    });
  }

  const body = (await res.json()) as FeJwtResponse & TokenResponse;
  return parseAuthTokenResponse(body);
}

export function mapUserInfo(userinfo: UserInfoResponse): AuthUser {
  const name =
    [userinfo.given_name, userinfo.family_name].filter(Boolean).join(' ') ||
    userinfo.name ||
    null;

  return {
    id: userinfo.sub,
    email: userinfo.email ?? null,
    name,
    picture: userinfo.picture ?? null,
    companyId: userinfo.current_active_company ?? null,
  };
}

export function mapIdTokenClaims(payload: Record<string, unknown>): AuthUser | null {
  const sub = payload.sub;
  if (typeof sub !== 'string' || !sub) return null;

  const givenName = typeof payload.given_name === 'string' ? payload.given_name : undefined;
  const familyName = typeof payload.family_name === 'string' ? payload.family_name : undefined;
  const nameField = typeof payload.name === 'string' ? payload.name : undefined;
  const name = nameField ?? ([givenName, familyName].filter(Boolean).join(' ') || null);

  return {
    id: sub,
    email: typeof payload.email === 'string' ? payload.email : null,
    name,
    picture: typeof payload.picture === 'string' ? payload.picture : null,
    companyId: resolveCompanyIdFromClaims(payload),
  };
}

export function decodeIdToken(idToken: string): Record<string, unknown> | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    return JSON.parse(Buffer.from(payload, 'base64url').toString('utf8')) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export class AppDirectClient {
  constructor(
    private readonly config: ResolvedConfig,
    private readonly fetchFn: FetchFn = fetch,
  ) {}

  buildAuthorizeUrl(state: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(' '),
      state,
    });
    return `${this.config.endpoints.authorizeUrl}?${params}`;
  }

  async exchangeAuthorizationCode(code: string): Promise<TokenResponse> {
    const res = await this.fetchFn(this.config.endpoints.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: basicAuthHeader(this.config.clientId, this.config.clientSecret),
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.config.redirectUri,
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new AuthError('token_exchange', 'Authorization code exchange failed', {
        details: { status: res.status, body },
      });
    }

    return (await res.json()) as TokenResponse;
  }

  async refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
    const res = await this.fetchFn(this.config.endpoints.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: basicAuthHeader(this.config.clientId, this.config.clientSecret),
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      throw new AuthError('refresh_failed', 'Refresh token exchange failed', {
        details: { status: res.status, body },
      });
    }

    return (await res.json()) as TokenResponse;
  }

  async exchangeAuthToken(oauthAccessToken: string): Promise<AuthTokenResult> {
    return postAuthEndpoint(
      this.fetchFn,
      this.config.endpoints.authTokenUrl,
      oauthAccessToken,
    );
  }

  async refreshAuthToken(authRefreshToken: string): Promise<AuthTokenResult> {
    return postAuthEndpoint(
      this.fetchFn,
      this.config.endpoints.authRefreshUrl,
      authRefreshToken,
    );
  }

  async fetchUserInfo(accessToken: string): Promise<AuthUser | null> {
    const res = await this.fetchFn(this.config.endpoints.userinfoUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!res.ok) return null;
    const userinfo = (await res.json()) as UserInfoResponse;
    return mapUserInfo(userinfo);
  }

  async resolveUser(accessToken: string, idToken?: string): Promise<AuthUser> {
    const fromUserInfo = await this.fetchUserInfo(accessToken);

    let fromIdToken: AuthUser | null = null;
    if (idToken) {
      const claims = decodeIdToken(idToken);
      if (claims) {
        fromIdToken = mapIdTokenClaims(claims);
      }
    }

    if (fromUserInfo?.id) {
      if (
        !fromUserInfo.companyId &&
        fromIdToken?.companyId &&
        fromIdToken.id === fromUserInfo.id
      ) {
        return { ...fromUserInfo, companyId: fromIdToken.companyId };
      }
      return fromUserInfo;
    }

    if (fromIdToken?.id) return fromIdToken;

    throw new AuthError('userinfo', 'Unable to resolve user identity');
  }

  async assertUserAssignedToApplication(input: {
    fejwt: string;
    companyUuid: string;
    userUuid: string;
    applicationId: number;
  }): Promise<void> {
    const url = new URL(
      `${this.config.issuerBaseUrl}/api/account/v2/companies/${encodeURIComponent(input.companyUuid)}/memberships/${encodeURIComponent(input.userUuid)}/myapps`,
    );
    url.searchParams.set('applicationId', String(input.applicationId));

    const res = await this.fetchFn(url.toString(), {
      headers: {
        Authorization: `Bearer ${input.fejwt}`,
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      await res.text().catch(() => undefined);
      throw new AuthError('unauthorized', 'User is not assigned to this application', {
        details: { status: res.status },
      });
    }

    const body = (await res.json()) as { content?: unknown[] };
    if (!Array.isArray(body.content) || body.content.length === 0) {
      throw new AuthError('unauthorized', 'User is not assigned to this application');
    }
  }
}
